/**
 * Dynamic Modal Fetcher
 */
function getCartRequestRoot() {
  return window.Shopify?.routes?.root || '/';
}

function getCartBundledSections() {
  const sections = [];

  document.documentElement.dispatchEvent(new CustomEvent('cart:prepare-bundled-sections', {
    bubbles: true,
    detail: { sections }
  }));

  return sections;
}

function openCartDrawerOrRedirect() { 
  const cartDrawer = document.querySelector('cart-drawer'); 
  if (cartDrawer && typeof cartDrawer.show === 'function') { 
    cartDrawer.show(); 
  } else { 
    window.location.href = `${getCartRequestRoot()}cart`; 
  } 
}

async function addItemsToCart(items, options = {}) {
  const root = getCartRequestRoot();
  const sections = getCartBundledSections();
  const body = {
    items,
    sections: sections.join(','),
    sections_url: `${root}cart`
  };

  if (options.attributes) {
    body.attributes = options.attributes;
  }

  const response = await fetch(`${root}cart/add.js`, { 
    method: 'POST', 
    headers: {
      'Content-Type': 'application/json',
      'X-Requested-With': 'XMLHttpRequest'
    }, 
    body: JSON.stringify(body)
  });
  const responseJson = await response.json();

  if (!response.ok) {
    throw new Error(responseJson.description || 'Add to cart failed');
  }

  const cart = await (await fetch(`${root}cart.js`)).json();
  cart.sections = responseJson.sections || {};
      
  document.documentElement.dispatchEvent(new CustomEvent('cart:change', {
    bubbles: true,
    detail: {
      baseEvent: 'variant:add',
      onSuccessDo: options.onSuccessDo,
      cart
    }
  }));
  document.dispatchEvent(new Event('cart:refresh'));

  return cart;
}

/*
 * variantId may be one id, an array of ids, or a comma-separated string of them — one line item each.
 *
 * The list form exists for the CUBE Collections: Shopify caps a fixed bundle at 30 components, so a collection
 * above that is several bundle variants that have to be bought together. Every existing caller passes a single
 * id and is unaffected; note that before this, a comma-separated string would have gone through parseInt and
 * silently added only the first one. Since 2026-10-15 no collection is split - each is one variant that the cart
 * transform expands into its cities (cityframes-cart-transform, wallOperations, up to 150) - so the list form has
 * no caller that needs it today; it stays, harmless, for a set past what one expand can carry.
 *
 * PROPERTIES ride along on every line the call adds, from `options.properties` or, for a card whose button is
 * written in Liquid, from `data-atc-properties` on the button as JSON. A FRAME, GRAND or BLOK line carries the
 * shipping estimate (`data-atc-ship`, worked out at the click) and a GRAND line its frame and background too, because a
 * line added from a card has to reach the cart — and the workshop — saying exactly what one added from the product
 * page says.
 *
 * `data-atc-group-key` names a property that gets a fresh token on every click. The GRAND's cart transform
 * groups on `_grand`, so two GRANDs added from the same card must not share one, or the second merges into the
 * first and the customer is charged for one.
 */
window.addCubeVariantToCart = async function(eventOrButton, variantId, options = {}) {
  const event = eventOrButton instanceof Event ? eventOrButton : null;
  const button = event ? event.currentTarget : eventOrButton;

  if (event) {
    event.preventDefault();
    event.stopPropagation();
  }

  const variantIds = (Array.isArray(variantId) ? variantId : String(variantId ?? '').split(','))
    .map((id) => parseInt(String(id).trim(), 10))
    .filter((id) => Number.isFinite(id) && id > 0);

  if (!variantIds.length || button?.disabled) return;

  const label = button?.querySelector('.heading') || button;
  const originalLabel = label?.textContent;
  const atcSource = options.source || button?.dataset?.atcSource || 'global_atc';
  const attributes = {};
  attributes[atcSource] = 'true';

  try {
    if (button) {
      button.disabled = true;
      button.setAttribute('aria-busy', 'true');
    }
    // The button carries the translated word; this file is an asset and cannot call `t`.
    // Falls back to English only if a caller forgets the attribute.
    if (label) label.textContent = button?.dataset?.addingLabel || 'Adding...';

    let properties = { ...(options.properties || {}) };

    const written = button?.dataset?.atcProperties;
    if (written) {
      try {
        properties = { ...properties, ...JSON.parse(written) };
      } catch (parseError) {
        console.error('Add to cart: data-atc-properties is not valid JSON', parseError);
      }
    }

    // A FRAME, GRAND or BLOK card adds the made-to-order ship window its product page adds ("Estimated Shipping"),
    // worked out now by the store's shipping calendar (assets/cf-ship.js) rather than printed into the cached card
    // (2026-10-23). A row already written for it (GRAND's, empty) keeps its place; GRAND's `_grand_meta` copy of the
    // visible rows is written again with it.
    const shipKind = button?.dataset?.atcShip;
    const shipLabel = window.CF_SHIP?.estimate?.label;
    if (shipKind && shipLabel) {
      const shipText = window.cfShip && window.cfShip.estimate(shipKind);
      if (shipText) properties[shipLabel] = shipText;
      else delete properties[shipLabel];
      if (properties._grand_meta) {
        properties._grand_meta = JSON.stringify(Object.fromEntries(Object.entries(properties).filter(([k]) => k.charAt(0) !== '_')));
      }
    }

    const groupKey = options.groupKey || button?.dataset?.atcGroupKey;
    if (groupKey) {
      properties[groupKey] = 'g' + Date.now().toString(36) + Math.random().toString(36).slice(2, 8);
    }

    await addItemsToCart(
      variantIds.map((id) => ({ id, quantity: 1, properties: { ...properties } })),
      {
        attributes
      }
    );

    openCartDrawerOrRedirect();
  } catch (error) {
    console.error('Add to cart failed', error);
    alert(error.message || 'Error adding to cart');
  } finally {
    if (button) {
      button.disabled = false;
      button.removeAttribute('aria-busy');
    }
    if (label && originalLabel) label.textContent = originalLabel;
  }
};
